import { useCallback, useEffect, useRef, useState } from 'react';

import NewCustomDataTable from '../common/NewCustomDataTable';
import { toast } from '../common/Toast/toast';
import { silenceFor } from '../common/Loader/busy';
import { accessErrorMessage, getAccessRequests, getAccessRules } from '../../../services/AccessRequestService';
import AccessRequestModal from './AccessRequestModal.jsx';
import AccessRuleModal from './AccessRuleModal.jsx';
import { StatusPill, formatWhen, roleNames } from './accessStatus.jsx';
import './access-center.css';

const PAGE_SIZE = 10;

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Declined' },
  { value: 'cancelled', label: 'Withdrawn' },
  { value: '', label: 'All' },
];

/**
 * The admin dashboard's Access tab: the requests waiting on a decision, and
 * the rules that decide who may ask for what. Filtering, search and paging
 * are all done by the API.
 */
const AccessRequestsAdmin = () => {
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);

  // Which load an answer belongs to: a slow reply must not land on top of a
  // filter chosen after it.
  const seq = useRef(0);

  const load = useCallback(async (nextPage, nextStatus, nextSearch) => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const result = await getAccessRequests({
        page: nextPage,
        limit: PAGE_SIZE,
        status: nextStatus,
        search: nextSearch,
      });
      if (mine !== seq.current) return;
      setRows(result.data);
      setTotal(result.pagination?.total || 0);
      setPage(nextPage);
    } catch (error) {
      if (mine === seq.current) toast.error(accessErrorMessage(error, 'Could not load access requests'));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      // The rules table shows its own skeleton rows.
      silenceFor();
      const result = await getAccessRules();
      setRules(result.roles || []);
    } catch (error) {
      toast.error(accessErrorMessage(error, 'Could not load the access rules'));
    } finally {
      setRulesLoading(false);
    }
  }, []);

  // A filter applies at once; typing waits for a pause.
  useEffect(() => {
    const timer = setTimeout(() => load(1, status, search.trim()), search.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [status, search, load]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const closeRequest = useCallback(() => setViewing(null), []);
  const closeRule = useCallback(() => setEditingRule(null), []);

  const requestHeaders = [
    {
      title: 'Requester',
      prop: 'userName',
      cell: (r) => (
        <span className="td-person-id">
          <span className="td-person-name">{r.userName || 'Unknown user'}</span>
          <span className="td-person-mail">{r.userEmail || '—'}</span>
        </span>
      ),
    },
    { title: 'Asks for', prop: 'requestedRoleLabel', cell: (r) => r.requestedRoleLabel || '—' },
    { title: 'Holds now', prop: 'currentRoleIds', cell: (r) => roleNames(r.currentRoles) },
    { title: 'Asked on', prop: 'createdAt', cell: (r) => formatWhen(r.createdAt) },
    { title: 'Status', prop: 'status', cell: (r) => <StatusPill status={r.status} /> },
  ];

  const ruleHeaders = [
    { title: 'Role', prop: 'roleLabel' },
    {
      title: 'Can request',
      prop: 'requestable',
      cell: (r) => (r.requestable?.length ? r.requestable.map((x) => x.label).join(', ') : 'Nothing'),
    },
  ];

  return (
    <div className="acc-page">
      <section className="td-panel">
        <div className="td-panel-head">
          <h3 className="td-panel-title">Access requests</h3>
          <div className="acc-tools">
            <select
              className="pg-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Filter by status"
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.label} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <input
              type="search"
              className="pg-input"
              placeholder="Search name, email or role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search access requests"
            />
          </div>
          <p className="td-panel-sub">
            Approving a request adds the role to that user&rsquo;s account.
          </p>
        </div>

        <div className="td-list td-list--access">
          <NewCustomDataTable
            data={rows}
            headers={requestHeaders}
            totalRecords={total}
            currentPage={page}
            recordsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(p) => load(p, status, search.trim())}
            pageTitle="access requests"
            addButton={false}
            exportButton={false}
            showToolbar={false}
            serverSide
            loading={loading}
            onRowClick={(row) => setViewing(row)}
            validRowClickTagNames="*"
          />
        </div>
      </section>

      <section className="td-panel">
        <div className="td-panel-head">
          <h3 className="td-panel-title">Who can ask for what</h3>
          <p className="td-panel-sub">
            A user can request only the roles listed against a role they already hold. Open a role to
            change its list.
          </p>
        </div>

        <div className="td-list td-list--access">
          <NewCustomDataTable
            data={rules}
            headers={ruleHeaders}
            totalRecords={rules.length}
            currentPage={1}
            recordsPerPage={Math.max(rules.length, 1)}
            onPageChange={() => {}}
            pageTitle="access rules"
            addButton={false}
            exportButton={false}
            showToolbar={false}
            showFooter={false}
            serverSide
            loading={rulesLoading}
            onRowClick={(row) => setEditingRule(row)}
            validRowClickTagNames="*"
          />
        </div>
      </section>

      {viewing && (
        <AccessRequestModal
          record={viewing}
          onClose={closeRequest}
          onDecided={() => {
            // Reload only: the popup shows its done panel and closes itself
            // through onClose.
            load(page, status, search.trim());
          }}
        />
      )}

      {editingRule && (
        <AccessRuleModal
          rule={editingRule}
          roles={rules}
          onClose={closeRule}
          onSaved={() => {
            loadRules();
          }}
        />
      )}
    </div>
  );
};

export default AccessRequestsAdmin;
